import React from 'react';
import { Box, Typography } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import { VA_TYPE_IDS } from './HomeDashboard';
import CompactAlertCard from '../cards/CompactAlertCard';

/**
 * AlertCountSidebar — premium right rail for VA alert types.
 *
 * Uses fixed-height compact cards (no flex-stretch) so the cards look
 * uniform and elegant regardless of how many there are. The rail itself
 * scrolls internally if the list overflows.
 */
const AlertCountSidebar = ({ detectionTypes, getCount, selectedId, onSelect }) => {
  const vaTypes = detectionTypes.filter(t => VA_TYPE_IDS.includes(t.id));
  const total = vaTypes.reduce((s, t) => s + getCount(t.id), 0);

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 232, lg: 248 },
        flexShrink: 0,
        height: { xs: 'auto', md: '100%' },
        background:
          'linear-gradient(180deg, rgba(20,32,46,0.85) 0%, rgba(14,22,32,0.85) 100%)',
        border: '1px solid rgba(26,188,156,0.22)',
        borderRadius: '14px',
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        boxShadow:
          '0 8px 28px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        overflow: 'hidden',
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.8,
          mb: 1.2,
          pb: 0.9,
          borderBottom: '1.5px solid rgba(26,188,156,0.2)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 4, height: 18, borderRadius: 1, backgroundColor: '#1ABC9C', flexShrink: 0, boxShadow: '0 0 8px rgba(26,188,156,0.6)' }} />
        <VideocamIcon sx={{ color: '#1ABC9C', fontSize: '0.95rem' }} />
        <Typography sx={{ color: '#1ABC9C', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', userSelect: 'none' }}>
          VA Alerts
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {total > 0 && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#E74C3C',
                boxShadow: '0 0 6px #E74C3C',
                animation: 'sideDot 1.4s ease-in-out infinite',
                '@keyframes sideDot': {
                  '0%,100%': { opacity: 1 },
                  '50%': { opacity: 0.25 },
                },
              }}
            />
          )}
          <Typography sx={{ fontSize: '0.62rem', color: total > 0 ? '#E74C3C' : '#3d5166', fontWeight: 700, letterSpacing: '0.05em' }}>
            {total > 0 ? `${total} LIVE` : 'NO EVENTS'}
          </Typography>
        </Box>
      </Box>

      {/* Card list — fixed compact rows so they never look stretched.
          The container itself scrolls when overflowing. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.7,
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 0.4,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(26,188,156,0.25)', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(26,188,156,0.45)' },
        }}
      >
        {vaTypes.map(type => (
          <Box key={type.id} sx={{ flexShrink: 0 }}>
            <CompactAlertCard
              id={type.id}
              title={type.title}
              count={getCount(type.id)}
              selected={selectedId === type.id}
              onClick={() => onSelect(type.id)}
              compact={true}
              fillHeight={false}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AlertCountSidebar;
